// The level builder's controls: actions, default keys and the reverse lookup the builder uses.
// Rebindable in Settings, like the game's controls (src/input/keybinds.ts). Flying uses the game's
// own movement keys; these only apply while building.

export type EditorAction =
  | 'selectTool'
  | 'place'
  | 'remove'
  | 'pickLook'
  | 'kind1'
  | 'kind2'
  | 'kind3'
  | 'kind4'
  | 'kind5'
  | 'kind6'
  | 'kind7'
  | 'catalog'
  | 'shape'
  | 'rotate'
  | 'texture'
  | 'colour'
  | 'motion'
  | 'sky'
  | 'wider'
  | 'narrower'
  | 'deeper'
  | 'shallower'
  | 'taller'
  | 'shorter'
  | 'grid'
  | 'copy'
  | 'paste'
  | 'undo'
  | 'redo'
  | 'showGrid'
  | 'showPanel'
  | 'playTest';

export interface EditorActionInfo {
  id: EditorAction;
  label: string;
  group: 'Place' | 'Shape and look' | 'Size' | 'Edit' | 'View and test';
  /** Held arrow-style keys repeat while held. */
  repeat?: boolean;
}

export const EDITOR_ACTIONS: readonly EditorActionInfo[] = [
  { id: 'selectTool', label: 'Select tool (click pieces to select and move them)', group: 'Place' },
  { id: 'place', label: 'Place (Place tool) / select (Select tool)', group: 'Place' },
  { id: 'remove', label: 'Delete the selection (or the piece under the cursor)', group: 'Place' },
  { id: 'pickLook', label: 'Copy the look you aim at', group: 'Place' },
  { id: 'kind1', label: 'Bhop block', group: 'Place' },
  { id: 'kind2', label: 'Solid', group: 'Place' },
  { id: 'kind3', label: 'Checkpoint', group: 'Place' },
  { id: 'kind4', label: 'Start', group: 'Place' },
  { id: 'kind5', label: 'End', group: 'Place' },
  { id: 'kind6', label: 'Booster', group: 'Place' },
  { id: 'kind7', label: 'Decoration', group: 'Place' },
  { id: 'catalog', label: 'Catalog (all pieces, textures, scenery)', group: 'Place' },
  { id: 'shape', label: 'Next shape (Shift: back)', group: 'Shape and look' },
  { id: 'rotate', label: 'Rotate', group: 'Shape and look' },
  { id: 'texture', label: 'Next texture (Shift: back)', group: 'Shape and look' },
  { id: 'colour', label: 'Next colour (Shift: back)', group: 'Shape and look' },
  { id: 'motion', label: 'Motion (moving pieces)', group: 'Shape and look' },
  { id: 'sky', label: 'Next sky (Shift: back)', group: 'Shape and look' },
  { id: 'wider', label: 'Wider', group: 'Size', repeat: true },
  { id: 'narrower', label: 'Narrower', group: 'Size', repeat: true },
  { id: 'deeper', label: 'Deeper (Shift: taller)', group: 'Size', repeat: true },
  { id: 'shallower', label: 'Shallower (Shift: shorter)', group: 'Size', repeat: true },
  { id: 'taller', label: 'Taller', group: 'Size', repeat: true },
  { id: 'shorter', label: 'Shorter', group: 'Size', repeat: true },
  { id: 'grid', label: 'Grid size', group: 'Size' },
  { id: 'copy', label: 'Copy (hold and sweep to pick more)', group: 'Edit' },
  { id: 'paste', label: 'Paste', group: 'Edit' },
  { id: 'undo', label: 'Undo', group: 'Edit' },
  { id: 'redo', label: 'Redo', group: 'Edit' },
  { id: 'showGrid', label: 'Show / hide the grid', group: 'View and test' },
  { id: 'showPanel', label: 'Show / hide this panel', group: 'View and test' },
  { id: 'playTest', label: 'Play test', group: 'View and test' },
];

export type EditorBinds = Record<EditorAction, string[]>;

export const MAX_EDITOR_BINDS = 2;

export function defaultEditorBinds(): EditorBinds {
  return {
    selectTool: ['KeyQ'],
    place: ['Mouse0'],
    remove: ['Delete', 'Backspace'],
    pickLook: ['KeyE'],
    kind1: ['Digit1'],
    kind2: ['Digit2'],
    kind3: ['Digit3'],
    kind4: ['Digit4'],
    kind5: ['Digit5'],
    kind6: ['Digit6'],
    kind7: ['Digit7'],
    catalog: ['KeyT'],
    shape: ['KeyF'],
    rotate: ['KeyR'],
    texture: ['KeyX'],
    colour: ['KeyG'],
    motion: ['KeyJ'],
    sky: ['KeyH'],
    wider: ['ArrowRight'],
    narrower: ['ArrowLeft'],
    deeper: ['ArrowUp'],
    shallower: ['ArrowDown'],
    taller: ['PageUp'],
    shorter: ['PageDown'],
    grid: ['KeyB'],
    copy: ['KeyC'],
    paste: ['KeyV'],
    undo: ['KeyZ'],
    redo: ['KeyY'],
    showGrid: ['KeyN'],
    showPanel: ['KeyI'],
    playTest: ['KeyP'],
  };
}

/** Saved settings -> complete binds (defaults for anything missing, no duplicates). */
export function sanitizeEditorBinds(raw: unknown): EditorBinds {
  const out = defaultEditorBinds();
  if (!raw || typeof raw !== 'object') return out;
  const seen = new Set<string>();
  const missing: EditorAction[] = [];
  const take = (codes: unknown[]) => {
    const list: string[] = [];
    for (const c of codes) {
      if (typeof c !== 'string' || !c || seen.has(c) || list.length >= MAX_EDITOR_BINDS) continue;
      seen.add(c);
      list.push(c);
    }
    return list;
  };
  // The middle button looks around and the right button opens the piece menu: they can't be bound.
  seen.add('Mouse1');
  seen.add('Mouse2');
  // M plays and mutes music everywhere now: Motion moved to J.
  const saved = raw as Record<string, unknown>;
  if (Array.isArray(saved.motion) && saved.motion.includes('KeyM'))
    raw = { ...saved, motion: saved.motion.map((c) => (c === 'KeyM' ? 'KeyJ' : c)) };
  for (const a of EDITOR_ACTIONS) {
    const v = (raw as Record<string, unknown>)[a.id];
    if (!Array.isArray(v)) missing.push(a.id);
    else {
      out[a.id] = take(v);
      // Every saved key was taken away (e.g. the mouse buttons that are reserved now): use the
      // default keys, so the action isn't left without one. Unbound on purpose ([]) stays unbound.
      if (v.length && !out[a.id].length) missing.push(a.id);
    }
  }
  for (const a of missing) out[a] = take(defaultEditorBinds()[a]);
  return out;
}

/** Bind `code` to `action` (taking it from any other builder action). */
export function bindEditor(binds: EditorBinds, action: EditorAction, code: string, slot?: number) {
  for (const a of EDITOR_ACTIONS) binds[a.id] = binds[a.id].filter((c) => c !== code);
  const list = binds[action];
  if (slot !== undefined && slot < list.length) list[slot] = code;
  else if (list.length < MAX_EDITOR_BINDS) list.push(code);
  else list[list.length - 1] = code;
}

export function unbindEditor(binds: EditorBinds, action: EditorAction, code: string) {
  binds[action] = binds[action].filter((c) => c !== code);
}

export function editorLookup(binds: EditorBinds): Map<string, EditorAction> {
  const m = new Map<string, EditorAction>();
  for (const a of EDITOR_ACTIONS) for (const c of binds[a.id]) m.set(c, a.id);
  return m;
}
